import { html } from 'lit-element';

import { SimpleLitElement } from '@elab/components';
import connect from '@elab/core/store/connect';
import { actions as globalActions } from '@elab/core/store/global';
import { link } from '@elab/core/utils/htmlTemplates';

/**
 * Component display product information with image, product name, product short description
 *
 * @author Zhynzher Oleksandr
 *
 * @attr { String } [product-name] - name of the product
 * @attr { String } [product-short-description] - short description of the product
 * @attr { Boolean }  [wrap-in-link] - define should product name and image be wrapped in link
 * @attr { String }  [product-link] -  link to product page
 * @attr { String } [product-range-name] - product range name
 * @attr { String } [product-brand-name] - product brand name
 * @attr { Boolean }  [show-description] - define should product description be depicted
 * @attr { Boolean }  [show-added-date] - define should product added date be depicted
 * @attr { Boolean }  [show-range-name] - define should product range be depicted
 * @attr { Boolean }  [show-brand-name] - define should product brand be depicted
 * @attr { String }  [product-added-date] -  product added date
 *
 * HTML Structure Example
 *
 * <e2-wishlist-product-info
 *      product-thumbnail-image="Product thumbnail image url"
 *      product-name="Product name"
 *      product-short-description="Product short description"
 *      product-code="Product code"
 *      product-variant-matrix="Arrey of variants"
 * </e2-wishlist-product-info>
 *
 * */

export class WishlistProductInfo extends SimpleLitElement {
    static get properties() {
        return {
            productName: { type: String, attribute: 'product-name' },
            productCode: { type: String, attribute: 'product-code' },
            productLink: { type: String, attribute: 'product-link' },
            productShortDescription: {
                type: String,
                attribute: 'product-short-description',
            },
            productAddedDate: { type: String, attribute: 'product-added-date' },
            productVariantMatrix: {
                type: Array,
                attribute: 'product-variant-matrix',
            },
            showDescription: { type: Boolean, attribute: 'show-description' },
            showAddedDate: { type: Boolean, attribute: 'show-added-date' },
            showVariant: { type: Boolean, attribute: 'show-variant' },
            wrapInLink: { type: Boolean, attribute: 'wrap-in-link' },
            productRangeName: { type: String, attribute: 'product-range-name' },
            showRangeName: { type: Boolean, attribute: 'show-range-name' },
            productBrandName: { type: String, attribute: 'product-brand-name' },
            showBrandName: { type: Boolean, attribute: 'show-brand-name' },
            showPromotionLabel: { type: Boolean, attribute: 'show-promotion-label' },
            promotionLabel: { type: String, attribute: 'promotion-label' },
        };
    }

    constructor() {
        super();
        this.productVariantMatrix = [];
    }

    render() {
        return html`
            <div class="wishlist-product-info">
                ${this.showBrandName
                    ? html`
                          <div class="wishlist-product-info__brand-name">
                              ${this.productBrandName}
                          </div>
                      `
                    : null}
                ${this.showRangeName
                    ? link(
                          {
                              className: 'wishlist-product-info__range-name',
                              href: this.productLink,
                          },
                          this.productRangeName,
                      )
                    : null}
                ${this.wrapInLink && !this.showRangeName
                    ? link(
                          {
                              className: 'wishlist-product-info__name',
                              href: this.productLink,
                          },
                          this.productName,
                      )
                    : html`
                          <div class="wishlist-product-info__name">
                              ${this.productName}
                          </div>
                      `}
                ${this.showPromotionLabel && this.promotionLabel
                    ? html`
                          <div class="wishlist-product-info__promotion">
                              <span class="wishlist-product-info__promotion-label"
                                  >${this.promotionLabel}</span
                              >
                          </div>
                      `
                    : ''}
                ${this.showDescription
                    ? html`
                          <div class="wishlist-product-info__description">
                              ${this.productShortDescription}
                          </div>
                      `
                    : null}
                ${this.showAddedDate
                    ? html`
                          <div class="wishlist-product-info__added-date">
                              ${this.productAddedDate}
                          </div>
                      `
                    : null}
                ${this.showVariant && this.productVariantMatrix && this.productVariantMatrix.length
                    ? html`
                          <e2-wishlist-product-variant
                              class="wishlist-product-info__variant"
                              product-code="${this.productCode}"
                              .productVariantMatrix=${this.productVariantMatrix}
                          >
                          </e2-wishlist-product-variant>
                      `
                    : null}
            </div>
        `;
    }
}

const mapDispatchToEvents = dispatch => ({
    redirect: e => dispatch(globalActions.redirect(e.detail)),
});

export default () =>
    customElements.define(
        'e2-wishlist-product-info',
        connect(null, mapDispatchToEvents)(WishlistProductInfo),
    );
